'use client'

import { memo } from "react"
import { usePathname } from "next/navigation"
import { formatImageURL, formatPrice, getEffectivePrice, getStrikethroughPrice, getDisplayDiscounts, isDiscountBetter, isCustomerAuthActive, getBuyXGetYTagText, getSalePercent, getOtherVariantSaleType, getLoyaltyProductDiscount } from "@/utils/util"
import { Heart, LoaderCircle, Plus, Star } from "lucide-react"
import { navigateUrl } from "@/utils/client-request"
import { useCart, useWishlist, useProductCard, useImageCarousel } from "@/lib/commerce/hooks"
import { useSession } from "next-auth/react"
import VariantSelectionModal from "./VariantSelectionModal"
import { CardSwatches } from "./VariantOptions"
import NoImage from "@/components/blocks/NoImage"

export const CARD_ASPECT = 'aspect-3/4'

const badgeClass ='px-2 py-1 bg-background text-foreground text-[11px] leading-none tracking-wide tabular-nums'
const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-foreground'

function ProductCard({ product, vendor, showVariantName = false }: { product: any, vendor: any, showVariantName?: boolean }) {
  const variantName = product?.variant?.name
  const { data: session } = useSession()
  const pathname = usePathname()
  const { isInWishlist, toggle: toggleWishlistHook, loading: wishlistLoading } = useWishlist()
  const { isProductAvailable, availableVariant, loading, adding, showVariantModal, setShowVariantModal, handleAddToCart, handleVariantAddToCart, colors, activeColor, selectColor, cardProduct } = useProductCard(product, vendor, showVariantName)
  const localIsInWishlist = isInWishlist(product.id)
  const { hoveredImageIndex, setHoveredImageIndex, allImages, remainingCount, shouldLoadImage, hasImages, handleTouchStart, handleTouchEnd, onMouseEnter, onMouseLeave } = useImageCarousel(cardProduct)

  const productHref = navigateUrl(`/product/${product.customId}`, pathname)
  const canQuickAdd = vendor?.canPlaceOrder !== false && isProductAvailable

  const toggleWishlist = () => toggleWishlistHook({ productId: product.id, variantId: product.variant?.id })

  const discounts = getDisplayDiscounts(product, !!session?.user?.id, availableVariant?.id)
  const effectivePrice = getEffectivePrice(availableVariant, discounts, !!session?.user?.id)
  const strikethroughPrice = getStrikethroughPrice(availableVariant, discounts, !!session?.user?.id)
  const variantHasPriceSale = !!(availableVariant?.priceSale && availableVariant?.price && availableVariant.priceSale < availableVariant.price)
  const showsDiscountBadge = discounts?.length > 0 && isDiscountBetter(availableVariant, discounts, !!session?.user?.id)
  const { loyaltySummary, loyaltyCategoryParents } = useCart()
  const loyaltyDeal = getLoyaltyProductDiscount({ product, variant: availableVariant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: showsDiscountBadge })
  const displayEffective = loyaltyDeal ? Math.max(0, (effectivePrice ?? availableVariant?.price ?? 0) - loyaltyDeal.unitSaving) : effectivePrice
  const displayStrike = loyaltyDeal ? (availableVariant?.price ?? 0) : strikethroughPrice
  const otherVariantSale = (!variantHasPriceSale && !(showsDiscountBadge && isProductAvailable))
    ? getOtherVariantSaleType(product, availableVariant?.id, !!session?.user?.id)
    : null

  const badges: string[] = []
  if (!isProductAvailable) {
    badges.push('Дууссан')
  } else if (showsDiscountBadge) {
    discounts.forEach((discount: any) => {
      if (discount.type === 'productDiscount' && discount.valueType === 'percent' && discount.valuePercent) badges.push(discount.valuePercent >= 100 ? 'Үнэгүй' : `-${discount.valuePercent}%`)
      else if (discount.type === 'productDiscount' && discount.valueType === 'amount' && discount.valueAmount) badges.push(`-${formatPrice(discount.valueAmount)}`)
      else if (discount.type === 'buyXgetY') badges.push(getBuyXGetYTagText(discount))
    })
    if (!discounts.some((d: any) => d.type === 'productDiscount') && variantHasPriceSale && !loyaltyDeal?.saleOffset) badges.push(`-${getSalePercent(availableVariant)}%`)
    if (loyaltyDeal) badges.push(`Лояалти -${loyaltyDeal.percent}%`)
  } else {
    if (variantHasPriceSale && !loyaltyDeal?.saleOffset) badges.push(`-${getSalePercent(availableVariant)}%`)
    if (loyaltyDeal) badges.push(`Лояалти -${loyaltyDeal.percent}%`)
  }
  if (otherVariantSale && !loyaltyDeal) badges.push(otherVariantSale === 'discount' ? 'Урамшуулалтай' : 'Хямдралтай')

  const price = !isProductAvailable ? (
    <span className="text-muted-foreground">Дууссан</span>
  ) : availableVariant?.price > 0 ? (
    displayEffective !== null ? (
      <>
        <span>{displayEffective === 0 ? 'Үнэгүй' : formatPrice(displayEffective)}</span>
        <span className="line-through text-muted-foreground">{formatPrice(displayStrike)}</span>
      </>
    ) : (
      <span>{formatPrice(availableVariant?.price)}</span>
    )
  ) : null

  return (
    <div className="group h-full flex flex-col">
      <div className={`${CARD_ASPECT} w-full relative overflow-hidden bg-muted`} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {!hasImages ? <NoImage /> : allImages.map((img: string, index: number) => shouldLoadImage(index) && (
          <img key={index} src={formatImageURL(img)} alt={product?.name} loading="lazy" decoding="async" className={`w-full h-full object-cover object-center absolute inset-0 transition-opacity duration-500 ${hoveredImageIndex === index ? 'opacity-100' : 'opacity-0'}`} />
        ))}
        {remainingCount > 0 && hoveredImageIndex === 4 && (
          <div className="absolute inset-0 bg-black/40 grid place-items-center text-white text-sm">+{remainingCount} зураг</div>
        )}

        <a href={productHref} aria-label={product?.name} className={`absolute inset-0 ${focusRing}`} />

        {badges.length > 0 && (
          <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-1 pointer-events-none">
            {badges.map(badge => <span key={badge} className={badgeClass}>{badge}</span>)}
          </div>
        )}

        {isCustomerAuthActive(vendor) && (
          <button type="button" onClick={toggleWishlist} aria-label="Хүслийн жагсаалт" aria-pressed={localIsInWishlist} className={`absolute top-1.5 right-1.5 z-10 h-9 w-9 grid place-items-center text-foreground transition-opacity duration-150 ${localIsInWishlist ? '' : 'sm:opacity-0 sm:group-hover:opacity-100 focus-visible:opacity-100'} ${focusRing}`}>
            {wishlistLoading ? <LoaderCircle className="size-4.5 animate-spin" strokeWidth={1.5} /> : <Heart className={`size-4.5 ${localIsInWishlist ? 'fill-current' : ''}`} strokeWidth={1.5} />}
          </button>
        )}

        {allImages.length > 1 && (
          <div className={`absolute inset-x-0 ${canQuickAdd ? 'bottom-15' : 'bottom-0'} z-20 hidden sm:flex gap-1 px-3 pb-3 pt-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150`}>
            {allImages.map((_: string, index: number) => (
              <div key={index} onMouseEnter={() => setHoveredImageIndex(index)} className="flex-1 h-5 flex items-end pointer-events-auto cursor-pointer">
                <div className={`w-full h-px transition-colors duration-150 ${hoveredImageIndex === index ? 'bg-white' : 'bg-white/40'}`} />
              </div>
            ))}
          </div>
        )}
        {allImages.length > 1 && (
          <div className="absolute inset-x-0 bottom-0 z-20 sm:hidden flex justify-center gap-1 pb-2 pointer-events-none">
            {allImages.map((_: string, index: number) => (
              <div key={index} className={`w-3 h-px ${hoveredImageIndex === index ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
        )}

        {canQuickAdd && (
          <button type="button" onClick={handleAddToCart} disabled={adding} aria-label="Сагсанд хийх" className={`sm:hidden absolute bottom-2 right-2 z-30 size-9 grid place-items-center bg-background text-foreground disabled:opacity-60 ${focusRing}`}>
            {adding ? <LoaderCircle className="size-4.5 animate-spin" strokeWidth={1.5} /> : <Plus className="size-4.5" strokeWidth={1.5} />}
          </button>
        )}

        {canQuickAdd && (
          <div className="absolute inset-x-0 bottom-0 z-30 hidden sm:group-hover:block p-3">
            <button type="button" onClick={handleAddToCart} disabled={loading} className={`w-full h-10 bg-background text-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background disabled:opacity-60 ${focusRing}`}>
              {loading ? 'Нэмж байна…' : 'Сагсанд хийх'}
            </button>
          </div>
        )}
      </div>

      <CardSwatches colors={colors} active={activeColor} onSelect={selectColor} />

      <a href={productHref} className={`${colors.length > 0 ? 'mt-2' : 'mt-3'} block ${focusRing}`}>
        <div className="text-[13px] sm:text-sm leading-snug line-clamp-2">{product?.name}</div>
        {showVariantName && variantName && <div className="mt-0.5 text-[13px] text-muted-foreground">{variantName}</div>}
        {price && <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-[13px] sm:text-sm tabular-nums">{price}</div>}
        {vendor?.canReview && product.reviewCount > 0 && (
          <div className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
            <Star aria-hidden className="size-3 fill-current" />
            {product.averageRating || 0} ({product.reviewCount})
          </div>
        )}
      </a>

      <VariantSelectionModal isOpen={showVariantModal} onClose={() => setShowVariantModal(false)} product={cardProduct} vendor={vendor} onAddToCart={handleVariantAddToCart} />
    </div>
  )
}

export default memo(ProductCard)
